import React, { useState, useRef, useEffect } from 'react';

interface ComboSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  onAddNew?: (value: string) => void;
  placeholder?: string;
  allowUnknown?: boolean;
}

const ComboSelect: React.FC<ComboSelectProps> = ({
  options,
  value,
  onChange,
  onAddNew,
  placeholder = 'Select or type...',
  allowUnknown = true,
}) => {
  const [input, setInput] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // If typed value not in options and not empty, add it
        if (input.trim() && input !== value) {
          commitValue(input.trim());
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [input, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitValue = (val: string) => {
    if (val && !options.includes(val) && onAddNew) {
      onAddNew(val);
    }
    onChange(val);
  };

  const filtered = options.filter(opt =>
    opt.toLowerCase().includes(input.toLowerCase())
  );

  const handleSelect = (val: string) => {
    setInput(val);
    onChange(val);
    setIsOpen(false);
  };

  const handleUnknown = () => {
    setInput('');
    onChange('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (input.trim()) {
        commitValue(input.trim());
      }
      setIsOpen(false);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
        placeholder={placeholder}
      />
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto">
          {allowUnknown && (
            <button
              type="button"
              onClick={handleUnknown}
              className="w-full text-left px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Unknown
            </button>
          )}
          {filtered.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => handleSelect(opt)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900 ${opt === value ? 'bg-emerald-50 dark:bg-emerald-900 text-emerald-700 font-medium' : 'text-gray-900 dark:text-gray-100'}`}
            >
              {opt}
            </button>
          ))}
          {input.trim() && !options.includes(input.trim()) && (
            <button
              type="button"
              onClick={() => {
                commitValue(input.trim());
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900 border-t border-gray-100 dark:border-gray-700"
            >
              Add "{input.trim()}"
            </button>
          )}
          {filtered.length === 0 && !input.trim() && !allowUnknown && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComboSelect;
